"use client";

/**
 * Sign up – creates Business + Owner user; then user can log in.
 */
import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { getApiUrl } from "@/lib/api";
import { setToken } from "@/lib/auth";
import { evaluateSignupEmailTypo } from "@/lib/email-domain-typos";
import { fsElement } from "@/lib/fs";
import { ReminderNameNote } from "@/components/ReminderNameNote";

/** Public signup: hair / barber / beauty / nails only for now. Full list remains in admin. */
const BUSINESS_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "HAIR_SALON", label: "Hair salon" },
  { value: "BARBER_SHOP", label: "Barber shop" },
  { value: "BEAUTY_SPA", label: "Beauty/Cosmetic" },
  { value: "NAILS", label: "Nails" },
];

/** UI-only sentinel — never sent to the signup API. */
const BUSINESS_TYPE_NOT_LISTED = "__NOT_LISTED__";

const BUSINESS_TYPE_SELECT_OPTIONS: { value: string; label: string }[] = [
  ...BUSINESS_TYPE_OPTIONS,
  { value: BUSINESS_TYPE_NOT_LISTED, label: "My business type isn’t listed" },
];

function SignUpForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const referralCode = (searchParams.get("ref") || "").trim() || undefined;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessSlug, setBusinessSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [businessType, setBusinessType] = useState("HAIR_SALON");
  const [unlistedBusinessKind, setUnlistedBusinessKind] = useState("");
  const [bookingMode, setBookingMode] = useState<"APPOINTMENTS" | "WALK_IN" | "HYBRID">("HYBRID");
  const [acceptCashPayments, setAcceptCashPayments] = useState(true);
  const notListedSelected = businessType === BUSINESS_TYPE_NOT_LISTED;
  const [error, setError] = useState("");
  const [emailDomainHint, setEmailDomainHint] = useState("");
  const [suggestedDomain, setSuggestedDomain] = useState<string | null>(null);
  const [softSuggestion, setSoftSuggestion] = useState<string | null>(null);
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);

  function applyTypoGate(raw: string, confirmed: string | null): boolean {
    const result = evaluateSignupEmailTypo(raw, confirmed);
    if (result.status === "ok") {
      setEmailDomainHint("");
      setSuggestedDomain(null);
      setSoftSuggestion(null);
      return true;
    }
    if (result.status === "blocked") {
      setError(result.message);
      setSuggestedDomain(result.suggestion ?? null);
      setEmailDomainHint(result.suggestion ? `Did you mean ${result.suggestion}?` : "");
      setSoftSuggestion(null);
      setConfirmedEmail(null);
      return false;
    }
    setSoftSuggestion(result.suggestion);
    setSuggestedDomain(result.suggestion);
    setEmailDomainHint(result.message);
    setError("");
    return false;
  }

  function applySuggestedDomain(suggestion: string) {
    const at = email.lastIndexOf("@");
    const local = at >= 0 ? email.slice(0, at) : email;
    const next = `${local}@${suggestion}`;
    setEmail(next);
    setConfirmedEmail(null);
    setError("");
    applyTypoGate(next, null);
  }

  function buildUnlistedMailto(): string {
    const kind = unlistedBusinessKind.trim();
    const subject = encodeURIComponent("Book4 signup: business type not listed");
    const lines: string[] = [
      "Hi Book4,",
      "",
      "My business type isn’t listed on the signup form.",
      kind ? `What kind of business: ${kind}` : "What kind of business: (not specified)",
    ];
    if (businessName.trim()) lines.push(`Business name: ${businessName.trim()}`);
    if (email.trim()) lines.push(`Email: ${email.trim()}`);
    if (name.trim()) lines.push(`Name: ${name.trim()}`);
    lines.push("", "Thanks,");
    const body = encodeURIComponent(lines.join("\n"));
    return `mailto:hello@book4.co.uk?subject=${subject}&body=${body}`;
  }

  function continueWithGeneral() {
    setBusinessType("GENERAL");
    setError("");
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    if (businessType === BUSINESS_TYPE_NOT_LISTED) {
      setError("Please get in touch so we can set you up, or continue with General / Other.");
      return;
    }
    const submitted = String(new FormData(e.currentTarget).get("email") ?? "").trim() || email;
    if (submitted !== email) setEmail(submitted);
    if (!applyTypoGate(submitted, submitted === email ? confirmedEmail : null)) {
      return;
    }
    const apiUrl = getApiUrl();
    const res = await fetch(`${apiUrl}/api/auth/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        email: submitted,
        password,
        businessName,
        businessSlug: businessSlug || businessName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
        businessType,
        ...(referralCode ? { referralCode } : {}),
        ...((businessType === "BARBER_SHOP" || businessType === "HAIR_SALON") && {
          bookingMode,
          acceptCashPayments,
        }),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (data.code === "EMAIL_DOMAIN_TYPO") {
        setError(data.error ?? "That email domain looks like a typo.");
        if (data.suggestedDomain) {
          setSuggestedDomain(data.suggestedDomain);
          setEmailDomainHint(`Did you mean ${data.suggestedDomain}?`);
        }
        return;
      }
      setError(data.error ?? "Sign up failed");
      return;
    }
    // Auto-login: log in with same credentials and redirect to dashboard
    const loginRes = await fetch(`${apiUrl}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: submitted, password }),
      credentials: "include",
    });
    const loginData = await loginRes.json().catch(() => ({}));
    if (loginRes.ok && loginData.token) {
      setToken(loginData.token);
      router.push("/app/onboarding");
      router.refresh();
      return;
    }
    router.push("/auth/login?message=Account created. Please log in.");
    router.refresh();
  }

  return (
    <div className="min-h-screen flex flex-col bg-muted/30 p-4">
      <Link
        href="/"
        className="mb-4 shrink-0 self-start text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        ← Back to home
      </Link>
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold mb-4">Create your Book4 account</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-1">
              Your name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setConfirmedEmail(null);
                setSoftSuggestion(null);
                setSuggestedDomain(null);
                setEmailDomainHint("");
                if (error) setError("");
              }}
              onBlur={(e) => {
                const value = e.currentTarget.value;
                if (!value.trim()) return;
                applyTypoGate(value, value === email ? confirmedEmail : null);
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              required
            />
            {emailDomainHint && (
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                {emailDomainHint}{" "}
                {suggestedDomain && (
                  <>
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={() => applySuggestedDomain(suggestedDomain)}
                    >
                      Use suggested domain
                    </button>
                    {softSuggestion && !error && (
                      <>
                        {" · "}
                        <button
                          type="button"
                          className="underline"
                          onClick={() => {
                            setConfirmedEmail(email);
                            setEmailDomainHint("");
                          }}
                        >
                          Keep as typed
                        </button>
                      </>
                    )}
                  </>
                )}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="fs-mask w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              minLength={8}
              required
            />
          </div>
          <div>
            <label htmlFor="businessName" className="block text-sm font-medium mb-1">
              Business name
            </label>
            <input
              id="businessName"
              type="text"
              value={businessName}
              onChange={(e) => {
                setBusinessName(e.target.value);
                if (!slugManuallyEdited) {
                  setBusinessSlug(
                    e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")
                  );
                }
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              required
            />
            <ReminderNameNote businessName={businessName} />
          </div>
          <div>
            <label htmlFor="businessType" className="block text-sm font-medium mb-1">
              Business type
            </label>
            <select
              id="businessType"
              value={businessType}
              onChange={(e) => {
                const v = e.target.value;
                setBusinessType(v);
                setError("");
                if (v === "BARBER_SHOP" || v === "HAIR_SALON") {
                  setBookingMode("HYBRID");
                  setAcceptCashPayments(true);
                }
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {BUSINESS_TYPE_SELECT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              {businessType === "GENERAL" && (
                <option value="GENERAL">General / Other</option>
              )}
            </select>
          </div>
          {notListedSelected && (
            <div className="rounded-lg border border-input bg-muted/30 p-4 space-y-3">
              <p className="text-sm text-foreground">
                We support more businesses than this list shows. We can still set you up.
                Get in touch and we&apos;ll help you get started.
              </p>
              <div>
                <label htmlFor="unlistedBusinessKind" className="block text-sm font-medium mb-1">
                  What kind of business? <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <input
                  id="unlistedBusinessKind"
                  type="text"
                  value={unlistedBusinessKind}
                  onChange={(e) => setUnlistedBusinessKind(e.target.value)}
                  placeholder="e.g. dog grooming, physiotherapy, tutoring…"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <a
                  href={buildUnlistedMailto()}
                  className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
                  {...fsElement("Signup Unlisted Contact Mailto", { id: "signup-unlisted-mailto", section: "signup" })}
                >
                  Email hello@book4.co.uk
                </a>
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-muted/50"
                  {...fsElement("Signup Unlisted Contact Page", { id: "signup-unlisted-contact", section: "signup" })}
                >
                  Contact form
                </Link>
              </div>
              <div className="pt-1 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">
                  Prefer to continue now with a generic setup? You can refine the type with us later.
                </p>
                <button
                  type="button"
                  onClick={continueWithGeneral}
                  className="w-full sm:w-auto rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-muted/50"
                  {...fsElement("Signup Continue With General", { id: "signup-continue-general", section: "signup" })}
                >
                  Continue with General / Other
                </button>
              </div>
            </div>
          )}
          {(businessType === "BARBER_SHOP" || businessType === "HAIR_SALON") && (
            <>
              <div>
                <label htmlFor="bookingMode" className="block text-sm font-medium mb-1">
                  How do you take customers?
                </label>
                <select
                  id="bookingMode"
                  value={bookingMode}
                  onChange={(e) => setBookingMode(e.target.value as "APPOINTMENTS" | "WALK_IN" | "HYBRID")}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="WALK_IN">Walk-ins only, no appointments</option>
                  <option value="APPOINTMENTS">Appointments only, customers book online</option>
                  <option value="HYBRID">Both, walk-ins and appointments</option>
                </select>
              </div>
              <div className="flex items-center gap-4 rounded-lg border border-input bg-muted/30 p-4">
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    id="acceptCashPayments"
                    checked={acceptCashPayments}
                    onChange={(e) => setAcceptCashPayments(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-background border border-border rounded-full peer peer-checked:border-primary peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                </label>
                <label htmlFor="acceptCashPayments" className="text-sm cursor-pointer">
                  Cash only, I&apos;ll record payments in the app (no card setup needed)
                </label>
              </div>
            </>
          )}
          <div>
            <label htmlFor="businessSlug" className="block text-sm font-medium mb-1">
              Booking page URL
            </label>
            <div className="flex items-center rounded-md border border-input bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              book4.co.uk/book/
              <input
                id="businessSlug"
                type="text"
                value={businessSlug}
                onChange={(e) => {
                  setBusinessSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                  setSlugManuallyEdited(true);
                }}
                className="flex-1 bg-transparent min-w-0 outline-none"
                placeholder="your-business"
              />
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {!notListedSelected && (
            <button
              type="submit"
              className="w-full rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground"
              {...fsElement("Business Sign Up Submit Button", { id: "signup-submit", section: "signup" })}
            >
              Sign up
            </button>
          )}
        </form>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            href="/auth/login"
            className="underline"
            {...fsElement("Signup Log In Link", { id: "signup-login-link", section: "signup" })}
          >
            Log in
          </Link>
        </p>
      </div>
      </div>
    </div>
  );
}


export default function SignUpPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-muted/30 p-4"><div className="w-full max-w-md rounded-lg border bg-card p-6 animate-pulse" /></div>}>
      <SignUpForm />
    </Suspense>
  );
}
